import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircleX, Eye, RotateCcw, Search, X } from "lucide-react";
import PackAssignDropdown from "./PackAssignDropdown";
import ToggleSwitch from "./ToggleSwitch";
import MenuIconTooltip from "./MenuIconTooltip";
import CharacterScrollPane from "./LibraryScrollPane";
import { MENU_LIBRARY_TOOLTIPS } from "../constants/menuTooltips";
import { useAuth } from "./AuthGate";
import { useAccountTextScale } from "../constants/textScalePreference";
import { getAppPortalRoot } from "../utils/appPortal";
import { clamp, readFrameBox, readWindowPos, windowMetrics } from "../utils/libraryWindow";
import { useFloatingWindowLayer } from "../utils/floatingWindows";
import {
  assignLibraryPassage,
  createLibraryCharacter,
  deleteLibraryCharacter,
  fetchLibraryCharacterCatalog,
  fetchLibraryCharacters,
  fetchLibraryHighlights,
  fetchLibraryReading,
  unassignLibraryPassage,
  updateLibraryCharacter,
} from "../utils/libraryApi";
import { readLibraryPlace, writeLibraryPlace } from "../utils/libraryReading";
import { locateQuoteStarts } from "../utils/libraryHighlightText";
import { chaptersToPlain } from "../utils/libraryPlainText";
import { normalizeRegisterSearch } from "../utils/registerWordListDisplay";
import "./LibraryCharacters.css";

const POS_KEY = "vocabularium_library_characters_pos";
const readingTextCache = new Map();

const CHARACTER_SKELETON = [
  "Arcadi Apollonovitch",
  "Archibald Archibaldovitch",
  "Azazello",
  "Bengalski",
  "Berlioz",
  "Biezdomny",
  "Caïphe",
  "Dr Stravinski",
];

const PASSAGE_SKELETON = [
  "Mais, Archibald Archibaldovitch, répondit le portier vert de peur, comment pouvais-je l'empêcher d'entrer, puisqu'il est membre du Massolit",
  "Écoute, Nicolas, c'est la dernière fois ! Des portiers de ce genre, nous n'en avons que faire au restaurant. Tu n'es bon qu'à faire un bedeau",
  "Va chercher Pantaleon à l'office. Appelle la milice. Procès-verbal. Une voiture. Pour l'hôpital psychiatrique",
];

function CharacterMemoSkeleton() {
  return (
    <div aria-hidden="true">
      {CHARACTER_SKELETON.map((name, index) => (
        <div
          key={name}
          className={`library-characters__person library-characters__person--skel${
            index === 0 ? " library-characters__person--active" : ""
          }`}
        >
          <span className="library-characters__gender library-characters__skel-dot" />
          <span className="library-characters__person-name">
            <span className="library-characters__skel-ink">{name}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function PassageMemoSkeleton() {
  return (
    <div aria-hidden="true">
      {PASSAGE_SKELETON.map((quote) => (
        <div key={quote} className="library-characters__passage">
          <p className="library-characters__passage-text">
            <span className="library-characters__skel-ink">{quote}</span>
          </p>
          <div className="library-characters__passage-actions">
            <span className="menu-icon-tooltip library-characters__skel-icon">
              <span className="library-characters__icon-btn">
                <Eye size={16} strokeWidth={1.75} />
              </span>
            </span>
            <span className="library-characters__passage-tail">
              <span className="menu-icon-tooltip library-characters__skel-icon">
                <span className="library-characters__icon-btn">
                  <RotateCcw size={16} strokeWidth={1.75} />
                </span>
              </span>
              <span className="menu-icon-tooltip library-characters__skel-icon">
                <span className="library-characters__icon-btn">
                  <X size={16} strokeWidth={1.75} />
                </span>
              </span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
const VIEW_KEY = "vocabularium_library_characters_view";

const GENDER_DOT = {
  homme: "library-characters__dot--homme",
  femme: "library-characters__dot--femme",
  autre: "library-characters__dot--autre",
};

const GENDER_OPTIONS = [
  { value: "homme", label: "homme", dotClass: GENDER_DOT.homme },
  { value: "femme", label: "femme", dotClass: GENDER_DOT.femme },
  { value: "autre", label: "autre", dotClass: GENDER_DOT.autre },
];

function readView(bookId) {
  try {
    const all = JSON.parse(localStorage.getItem(VIEW_KEY) || "{}");
    const item = all[String(bookId)] || {};
    return {
      peopleScroll: Math.max(0, Math.round(Number(item.peopleScroll) || 0)),
      passagesScroll: Math.max(0, Math.round(Number(item.passagesScroll) || 0)),
      selectedId: String(item.selectedId || ""),
      lastCharacterId: String(item.lastCharacterId || ""),
    };
  } catch {
    return { peopleScroll: 0, passagesScroll: 0, selectedId: "", lastCharacterId: "" };
  }
}

function writeView(bookId, patch) {
  if (!bookId) return;
  let all = {};
  try {
    all = JSON.parse(localStorage.getItem(VIEW_KEY) || "{}");
  } catch {
    all = {};
  }
  all[String(bookId)] = { ...(all[String(bookId)] || {}), ...patch };
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify(all));
  } catch {
    /* le navigateur refuse l'enregistrement */
  }
}

function sortPassages(passages, order) {
  return [...(passages || [])].sort((left, right) => {
    const leftStart = order?.has(left.highlightId) ? order.get(left.highlightId) : null;
    const rightStart = order?.has(right.highlightId) ? order.get(right.highlightId) : null;
    const leftMissing = leftStart == null;
    const rightMissing = rightStart == null;
    if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
    if (!leftMissing && leftStart !== rightStart) return leftStart - rightStart;
    return String(left.createdAt || "").localeCompare(String(right.createdAt || ""));
  });
}

function tidyName(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

function readPos() {
  return readWindowPos(POS_KEY);
}

function highlightAnchor(item) {
  const quote = String(item?.quote || "").replace(/\s+/g, " ").trim();
  const before = String(item?.before || "").replace(/\s+/g, " ").trim();
  const after = String(item?.after || "").replace(/\s+/g, " ").trim();
  if (quote.length >= 24) return quote.slice(0, 120);
  return `${before.slice(-48)} ${quote} ${after.slice(0, 48)}`.replace(/\s+/g, " ").trim().slice(0, 120);
}

function passageHighlight(mark, highlights) {
  const id = mark?.dataset?.highlightId || "";
  if (!id || id.startsWith("pending-")) return null;
  const passage = mark.classList.contains("library-mark--passage");
  const favorite = mark.classList.contains("library-mark--favorite");
  if (!passage && !favorite) return null;
  const highlight = (highlights || []).find((item) => item.id === id);
  if (!highlight || (highlight.kind !== "passage" && highlight.kind !== "favorite")) return null;
  return highlight;
}

export default function LibraryCharacters({
  bookId: readingBookId = "",
  frameRef,
  highlights = [],
  marksReady = false,
  openToken = 0,
  onShowPassage = () => {},
  pickBook = false,
  onOpenBook = () => {},
}) {
  const { user } = useAuth();
  const [textScale] = useAccountTextScale(user?.sub || "");
  const windowGrow = textScale > 1 ? 20 : 0;
  const windowGrowRef = useRef(windowGrow);
  windowGrowRef.current = windowGrow;
  const dragRef = useRef(null);
  const openRef = useRef(false);
  const modeRef = useRef("browse");
  const posRef = useRef(readPos());
  const browseWasOpenRef = useRef(false);
  const seenTokenRef = useRef(openToken);
  const highlightsRef = useRef(highlights);
  const catalogCharactersRef = useRef(null);
  const pickedBookIdRef = useRef("");

  const [open, setOpen] = useState(false);
  const layer = useFloatingWindowLayer(open);
  const [mode, setMode] = useState("browse");
  const [characters, setCharacters] = useState([]);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [menu, setMenu] = useState(null);
  const [assignHighlight, setAssignHighlight] = useState(null);
  const [previousName, setPreviousName] = useState("");
  const [previousCharacterId, setPreviousCharacterId] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [characterId, setCharacterId] = useState("");
  const [formError, setFormError] = useState("");
  const [actionError, setActionError] = useState("");
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [leftRatio, setLeftRatio] = useState(posRef.current.leftRatio);
  const [topRatio, setTopRatio] = useState(posRef.current.topRatio);
  const [frameBox, setFrameBox] = useState(readFrameBox);
  const [passageOrder, setPassageOrder] = useState(null);
  const [passageQuery, setPassageQuery] = useState("");
  const passageQueryRef = useRef("");
  const passagesScrollRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [editError, setEditError] = useState("");
  const [memoBooks, setMemoBooks] = useState([]);
  const [pickedBookId, setPickedBookId] = useState("");
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksReady, setBooksReady] = useState(false);
  const [catalogTick, setCatalogTick] = useState(0);
  const [booksError, setBooksError] = useState("");
  const [bookHighlights, setBookHighlights] = useState([]);
  const bookId = pickBook ? pickedBookId : readingBookId;
  pickedBookIdRef.current = pickedBookId;
  const passageQueryBookRef = useRef(bookId);
  highlightsRef.current = pickBook ? bookHighlights : highlights;

  if (passageQueryBookRef.current !== bookId) {
    passageQueryBookRef.current = bookId;
    passageQueryRef.current = "";
    if (passageQuery) setPassageQuery("");
    if (characters.length) setCharacters([]);
    if (selectedId) setSelectedId("");
  }

  openRef.current = open;
  modeRef.current = mode;

  useEffect(() => {
    if (!pickBook || !open) return undefined;
    let cancelled = false;
    setBooksLoading(true);
    setBooksError("");
    fetchLibraryCharacterCatalog()
      .then((json) => {
        if (cancelled) return;
        const books = Array.isArray(json.books) ? json.books : [];
        const currentId = pickedBookIdRef.current;
        const next =
          currentId && books.some((book) => book.id === currentId)
            ? currentId
            : books[0]?.id || "";
        if (next && next === json.bookId) {
          const list = Array.isArray(json.characters) ? json.characters : [];
          catalogCharactersRef.current = { bookId: next, characters: list };
          setCharacters(list);
          setCharactersLoading(false);
        } else {
          catalogCharactersRef.current = null;
          setCharacters([]);
          setCharactersLoading(Boolean(next));
        }
        setMemoBooks(books);
        setPickedBookId(next);
        setCatalogTick((current) => current + 1);
      })
      .catch((err) => {
        if (!cancelled) setBooksError(err.message || "Impossible de lire les livres.");
      })
      .finally(() => {
        if (cancelled) return;
        setBooksLoading(false);
        setBooksReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pickBook, open]);

  useEffect(() => {
    if (!pickBook) return undefined;
    if (!bookId) {
      setBookHighlights([]);
      return undefined;
    }
    let cancelled = false;
    fetchLibraryHighlights(bookId)
      .then((json) => {
        if (!cancelled) setBookHighlights(Array.isArray(json.highlights) ? json.highlights : []);
      })
      .catch(() => {
        if (!cancelled) setBookHighlights([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pickBook, bookId]);

  useEffect(() => {
    const seeded = catalogCharactersRef.current;
    if (pickBook && bookId && seeded?.bookId === bookId) {
      catalogCharactersRef.current = null;
      setCharacters(seeded.characters);
      setCharactersLoading(false);
      setListError("");
      return undefined;
    }
    let cancelled = false;
    if (pickBook) setCharactersLoading(Boolean(bookId));
    setCharacters([]);
    setSelectedId("");
    setMenu(null);
    setEditing(false);
    setEditName("");
    setDeleteName("");
    setEditError("");
    setListError("");
    if (!pickBook) {
      setOpen(false);
      setMode("browse");
    }
    if (!bookId) return undefined;
    fetchLibraryCharacters(bookId)
      .then((json) => {
        if (cancelled) return;
        setCharacters(Array.isArray(json.characters) ? json.characters : []);
        setCharactersLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err.message || "Impossible de lire les personnages.");
        setCharactersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId, pickBook, catalogTick]);

  useEffect(() => {
    if (!characters.length) {
      setSelectedId("");
      return;
    }
    setSelectedId((current) => {
      if (current && characters.some((item) => item.id === current)) return current;
      const saved = readView(bookId).selectedId;
      if (saved && characters.some((item) => item.id === saved)) return saved;
      return characters[0].id;
    });
  }, [bookId, characters]);

  useEffect(() => {
    if (!bookId || !selectedId) return;
    if (!characters.some((item) => item.id === selectedId)) return;
    writeView(bookId, { selectedId });
  }, [bookId, selectedId, characters]);

  const passageOrderKey = useMemo(
    () =>
      characters
        .flatMap((character) => character.passages || [])
        .map((passage) => `${passage.highlightId}\t${passage.quote || ""}`)
        .join("\n"),
    [characters]
  );
  const highlightOrderKey = useMemo(() => {
    const source = pickBook ? bookHighlights : highlights;
    return source
      .map((item) => `${item.id}\t${item.before || ""}\t${item.after || ""}`)
      .join("\n");
  }, [pickBook, bookHighlights, highlights]);

  useEffect(() => {
    if (!bookId) {
      setPassageOrder(null);
      return undefined;
    }
    const passages = characters.flatMap((character) => character.passages || []);
    if (!passages.length) {
      setPassageOrder(new Map());
      return undefined;
    }
    let cancelled = false;
    const apply = (text) => {
      if (cancelled) return;
      const byId = new Map((highlightsRef.current || []).map((item) => [item.id, item]));
      const entries = passages.map((passage) => {
        const full = byId.get(passage.highlightId);
        return {
          quote: passage.quote || full?.quote || "",
          before: full?.before || "",
          after: full?.after || "",
        };
      });
      const starts = locateQuoteStarts(text, entries);
      const order = new Map();
      passages.forEach((passage, index) => {
        const start = starts[index];
        if (start == null) return;
        order.set(passage.highlightId, start);
      });
      setPassageOrder(order);
    };
    const cached = readingTextCache.get(bookId);
    if (typeof cached === "string") {
      apply(cached);
      return undefined;
    }
    fetchLibraryReading(bookId)
      .then((json) => {
        if (cancelled) return;
        const text = chaptersToPlain(json.chapters);
        readingTextCache.set(bookId, text);
        apply(text);
      })
      .catch(() => {
        if (!cancelled) setPassageOrder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId, characters, passageOrderKey, highlightOrderKey]);

  useEffect(() => {
    if (!marksReady) return;
    const ids = new Set(highlights.map((item) => item.id));
    setCharacters((current) =>
      current.map((character) => ({
        ...character,
        passages: (character.passages || []).filter((passage) => ids.has(passage.highlightId)),
      }))
    );
  }, [highlights, marksReady]);

  useEffect(() => {
    if (seenTokenRef.current === openToken) return;
    seenTokenRef.current = openToken;
    setMenu(null);
    setAssignHighlight(null);
    setFormError("");
    setName("");
    setGender("");
    setCharacterId("");
    setEditing(false);
    setMode("browse");
    setOpen((current) => !(current && modeRef.current === "browse"));
  }, [openToken]);

  useEffect(() => {
    const frame = frameRef?.current;
    if (!frame) return undefined;
    const onContext = (event) => {
      const mark = event.target.closest?.(".library-mark");
      if (!mark || !frame.contains(mark)) return;
      if (
        !mark.classList.contains("library-mark--passage") &&
        !mark.classList.contains("library-mark--favorite")
      ) {
        return;
      }
      event.preventDefault();
      const highlight = passageHighlight(mark, highlightsRef.current);
      if (!highlight) return;
      setMenu({ x: event.clientX, y: event.clientY, highlight });
    };
    frame.addEventListener("contextmenu", onContext);
    return () => frame.removeEventListener("contextmenu", onContext);
  }, [frameRef, bookId, marksReady]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = (event) => {
      if (event.target?.closest?.(".library-characters__menu")) return;
      setMenu(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setMenu(null);
    };
    const onScroll = () => setMenu(null);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menu]);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer = frame && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const box = readFrameBox();
      const size = windowMetrics(box, windowGrowRef.current);
      const maxLeft = Math.max(box.width - size.visualWidth, 0);
      const maxTop = Math.max(box.height - size.visualHeight, 0);
      const left = clamp(event.clientX - drag.offsetX - box.left, 0, maxLeft);
      const top = clamp(event.clientY - drag.offsetY - box.top, 0, maxTop);
      const nextLeft = maxLeft ? left / maxLeft : 0;
      const nextTop = maxTop ? top / maxTop : 0;
      posRef.current = { leftRatio: nextLeft, topRatio: nextTop };
      setLeftRatio(nextLeft);
      setTopRatio(nextTop);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setDragging(false);
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
      } catch {
        /* la position reste en mémoire pour cette visite */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const selected = characters.find((item) => item.id === selectedId) || null;
  const normalizedPassageQuery = normalizeRegisterSearch(passageQuery);
  const visiblePassages = useMemo(() => {
    const sorted = sortPassages(selected?.passages, passageOrder);
    if (!normalizedPassageQuery) return sorted;
    return sorted.filter((passage) =>
      normalizeRegisterSearch(passage.quote).includes(normalizedPassageQuery)
    );
  }, [selected, passageOrder, normalizedPassageQuery]);

  useLayoutEffect(() => {
    if (passageQueryRef.current === normalizedPassageQuery) return;
    passageQueryRef.current = normalizedPassageQuery;
    const node = passagesScrollRef.current;
    if (node) node.scrollTop = 0;
  }, [normalizedPassageQuery]);

  useEffect(() => {
    setEditName(selected?.name || "");
    setDeleteName("");
    setEditError("");
  }, [selectedId, selected?.name]);
  const options = useMemo(
    () =>
      characters.map((item) => ({
        value: item.id,
        label: item.name,
        dotClass: GENDER_DOT[item.gender] || GENDER_DOT.autre,
      })),
    [characters]
  );
  const reassignOptions = useMemo(
    () => options.filter((item) => item.value !== previousCharacterId),
    [options, previousCharacterId]
  );

  const windowStyle = useMemo(() => {
    const size = windowMetrics(frameBox, windowGrow);
    const maxLeft = Math.max(frameBox.width - size.visualWidth, 0);
    const maxTop = Math.max(frameBox.height - size.visualHeight, 0);
    return {
      width: `${size.designWidth}px`,
      height: `${size.designHeight}px`,
      minWidth: `${size.designWidth}px`,
      minHeight: `${size.designHeight}px`,
      left: `${frameBox.left + leftRatio * maxLeft}px`,
      top: `${frameBox.top + topRatio * maxTop}px`,
      transform: `scale(${size.scale})`,
      transformOrigin: "top left",
    };
  }, [frameBox, leftRatio, topRatio, windowGrow]);

  const startDrag = (event) => {
    if (
      event.target.closest(
        "button, input, textarea, .pack-assign-dropdown, .library-characters__mode-switch, .library-characters__search, [role='switch']"
      )
    ) {
      return;
    }
    const windowEl = event.currentTarget.parentElement;
    if (!windowEl) return;
    const rect = windowEl.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setDragging(true);
    event.preventDefault();
  };

  const openForm = (nextMode, highlight) => {
    browseWasOpenRef.current = openRef.current && modeRef.current === "browse";
    const lastCharacterId = nextMode === "assign" ? readView(bookId).lastCharacterId : "";
    setAssignHighlight(highlight);
    setPreviousName("");
    setPreviousCharacterId("");
    setMode(nextMode);
    setName("");
    setGender("");
    setCharacterId(characters.some((item) => item.id === lastCharacterId) ? lastCharacterId : "");
    setFormError("");
    setMenu(null);
    setOpen(true);
    layer.bringToFront();
  };

  const openReassign = (passage, character) => {
    browseWasOpenRef.current = true;
    setAssignHighlight({
      id: passage.highlightId,
      quote: passage.quote,
      kind: passage.kind,
    });
    setPreviousName(character.name);
    setPreviousCharacterId(character.id);
    setCharacterId("");
    setFormError("");
    setActionError("");
    setMode("reassign");
  };

  const renameCharacter = async () => {
    if (!bookId || !selected || saving) return;
    const nextName = tidyName(editName);
    if (!nextName || nextName === selected.name) return;
    setSaving(true);
    setEditError("");
    try {
      const result = await updateLibraryCharacter(bookId, selected.id, { name: nextName });
      setCharacters(Array.isArray(result.characters) ? result.characters : []);
    } catch (err) {
      setEditError(err.message || "Impossible de renommer le personnage.");
    } finally {
      setSaving(false);
    }
  };

  const changeGender = async (nextGender) => {
    if (!bookId || !selected || saving || nextGender === (selected.gender || "autre")) return;
    setSaving(true);
    setEditError("");
    try {
      const result = await updateLibraryCharacter(bookId, selected.id, { gender: nextGender });
      setCharacters(Array.isArray(result.characters) ? result.characters : []);
    } catch (err) {
      setEditError(err.message || "Impossible de modifier le genre.");
    } finally {
      setSaving(false);
    }
  };

  const removeCharacter = async () => {
    if (!bookId || !selected || saving || deleteName !== selected.name) return;
    setSaving(true);
    setEditError("");
    try {
      const result = await deleteLibraryCharacter(bookId, selected.id);
      const nextCharacters = Array.isArray(result.characters) ? result.characters : [];
      setCharacters(nextCharacters);
      setDeleteName("");
      if (pickBook && !nextCharacters.length) {
        const remaining = memoBooks.filter((item) => item.id !== bookId);
        setMemoBooks(remaining);
        setPickedBookId(remaining[0]?.id || "");
      }
    } catch (err) {
      setEditError(err.message || "Impossible de supprimer le personnage.");
    } finally {
      setSaving(false);
    }
  };

  const unassign = async (highlightId) => {
    if (!bookId || saving) return;
    setSaving(true);
    setActionError("");
    try {
      const result = await unassignLibraryPassage(bookId, highlightId);
      setCharacters(Array.isArray(result.characters) ? result.characters : []);
    } catch (err) {
      setActionError(err.message || "Impossible de retirer le lien.");
    } finally {
      setSaving(false);
    }
  };

  const cancelForm = () => {
    setAssignHighlight(null);
    setFormError("");
    setName("");
    setGender("");
    setCharacterId("");
    if (browseWasOpenRef.current) {
      setMode("browse");
      return;
    }
    setMode("browse");
    setOpen(false);
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!bookId || !assignHighlight || saving) return;
    setSaving(true);
    setFormError("");
    try {
      const result =
        mode === "create"
          ? await createLibraryCharacter(bookId, {
              name,
              gender,
              highlightId: assignHighlight.id,
            })
          : await assignLibraryPassage(bookId, {
              characterId,
              highlightId: assignHighlight.id,
            });
      setCharacters(Array.isArray(result.characters) ? result.characters : []);
      setSelectedId(result.characterId || "");
      const chosenId = result.characterId || (mode === "create" ? "" : characterId);
      if (chosenId) writeView(bookId, { lastCharacterId: chosenId });
      setAssignHighlight(null);
      setName("");
      setGender("");
      setCharacterId("");
      setMode("browse");
      setOpen(true);
    } catch (err) {
      setFormError(err.message || "Impossible d'enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  const closeWindow = useCallback(() => {
    setOpen(false);
    setMode("browse");
    setAssignHighlight(null);
    setFormError("");
  }, []);

  const portalRoot = getAppPortalRoot();
  if (!portalRoot || (!bookId && !(pickBook && open))) return null;

  const menuStyle = menu
    ? {
        left: `${Math.max(8, Math.min(menu.x, window.innerWidth - 260))}px`,
        top: `${Math.max(8, Math.min(menu.y, window.innerHeight - 96))}px`,
      }
    : null;
  const title =
    mode === "create"
      ? "Nouveau personnage"
      : mode === "assign"
        ? "Personnage existant"
        : mode === "reassign"
          ? "Changer de personnage"
          : "Mémo des personnages";
  const bookOptions = memoBooks.map((item) => ({ value: item.id, label: item.title }));
  const charactersPending =
    pickBook && !booksError && !listError && !characters.length && (charactersLoading || !booksReady);
  const peopleEmpty = booksError
    ? booksError
    : pickBook && booksReady && !booksLoading && !memoBooks.length
      ? "Aucun mémo de personnage."
      : listError || "Aucun personnage.";
  const canValidate =
    mode === "create"
      ? Boolean(name.trim()) && Boolean(gender)
      : mode === "reassign"
        ? Boolean(characterId) && characterId !== previousCharacterId
        : Boolean(characterId);

  return createPortal(
    <div className="library-characters">
      {menu && menuStyle ? (
        <ul className="library-characters__menu" style={menuStyle} role="menu">
          <li>
            <button
              type="button"
              role="menuitem"
              disabled={!characters.length}
              onClick={() => openForm("assign", menu.highlight)}
            >
              associer à un personnage existant
            </button>
          </li>
          <li>
            <button
              type="button"
              role="menuitem"
              onClick={() => openForm("create", menu.highlight)}
            >
              associer à un nouveau personnage
            </button>
          </li>
        </ul>
      ) : null}

      {open ? (
        <section
          className="library-characters__window"
          style={{ ...windowStyle, zIndex: layer.zIndex }}
          aria-label="Mémo des personnages"
          onPointerDownCapture={layer.bringToFront}
        >
          <header
            className={`library-characters__banner${
              dragging ? " library-characters__banner--dragging" : ""
            }${mode === "browse" ? " library-characters__banner--browse" : ""}`}
            onPointerDown={startDrag}
          >
            {mode === "browse" ? (
              <ToggleSwitch
                compact
                className="library-characters__mode-switch"
                checked={editing}
                plain
                height={28}
                trackOnColor="#e07a1a"
                ariaLabel={
                  editing
                    ? "Mode modifier — basculer vers la liste"
                    : "Mode liste — basculer vers modifier"
                }
                onChange={setEditing}
              />
            ) : (
              <span />
            )}
            {pickBook && mode === "browse" ? (
              <div className="library-characters__book-picker">
                <PackAssignDropdown
                  options={bookOptions}
                  value={pickedBookId}
                  onChange={setPickedBookId}
                  disabled={!bookOptions.length}
                  placeholder={booksReady ? "Aucun livre" : "Chargement…"}
                  ariaLabel="Choisir un livre"
                  matchTriggerWidth
                  openBelow
                  menuMaxHeight={240}
                />
              </div>
            ) : (
              <h2 className="library-characters__title">{title}</h2>
            )}
            {mode === "browse" ? (
              <div className="library-characters__search">
                <Search size={14} strokeWidth={1.75} aria-hidden="true" />
                <input
                  type="search"
                  value={passageQuery}
                  placeholder="un passage…"
                  aria-label="Chercher dans les passages de ce personnage"
                  onChange={(event) => setPassageQuery(event.target.value)}
                />
                <button
                  type="button"
                  className="library-characters__search-clear"
                  aria-label="Effacer la recherche"
                  disabled={!passageQuery}
                  onClick={() => setPassageQuery("")}
                >
                  <CircleX size={14} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="library-characters__close"
              aria-label="Fermer les personnages"
              onClick={closeWindow}
            >
              <X aria-hidden="true" />
            </button>
          </header>

          {mode === "browse" ? (
            <div className="library-characters__body">
              <CharacterScrollPane
                className="library-characters__pane--people"
                scrollerClassName="library-characters__people"
                restoreKey={bookId ? `people:${bookId}` : ""}
                restoreTop={readView(bookId).peopleScroll}
                contentKey={characters.length}
                onScrollPosition={(scrollTop) =>
                  writeView(bookId, { peopleScroll: Math.max(0, Math.round(scrollTop)) })
                }
              >
                {charactersPending ? (
                  <>
                    <p className="library-characters__skel-status">Chargement des personnages</p>
                    <CharacterMemoSkeleton />
                  </>
                ) : characters.length ? (
                  characters.map((character) => (
                    <button
                      key={character.id}
                      type="button"
                      className={`library-characters__person${
                        character.id === selectedId ? " library-characters__person--active" : ""
                      }`}
                      onClick={() => {
                        if (character.id === selectedId) return;
                        writeView(bookId, { selectedId: character.id, passagesScroll: 0 });
                        setSelectedId(character.id);
                      }}
                    >
                      <span
                        className={`library-characters__gender library-characters__gender--${
                          character.gender || "autre"
                        }`}
                        aria-hidden="true"
                      />
                      <span className="library-characters__person-name">{character.name}</span>
                    </button>
                  ))
                ) : (
                  <p className="library-characters__empty">{peopleEmpty}</p>
                )}
              </CharacterScrollPane>
              {charactersPending ? (
                <CharacterScrollPane
                  className="library-characters__pane--passages"
                  scrollerClassName="library-characters__passages"
                  contentKey="skeleton"
                >
                  <PassageMemoSkeleton />
                </CharacterScrollPane>
              ) : editing ? (
              <div className="library-characters__passages library-characters__editor">
                  {selected ? (
                    <>
                      {editError ? <p className="library-characters__error">{editError}</p> : null}
                      <div className="library-characters__rename-row">
                        <p className="library-characters__label">Personnage</p>
                        <input
                          className="library-characters__name"
                          value={editName}
                          maxLength={80}
                          aria-label="Nom du personnage"
                          autoComplete="off"
                          onChange={(event) => setEditName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              renameCharacter();
                            }
                          }}
                        />
                      </div>
                      <div className="library-characters__editor-actions">
                        <button
                          type="button"
                          className="library-characters__button library-characters__button--ok"
                          disabled={saving || !tidyName(editName) || tidyName(editName) === selected.name}
                          onClick={renameCharacter}
                        >
                          Renommer
                        </button>
                      </div>
                      <p className="library-characters__label">Genre</p>
                      <div className="library-characters__genre">
                        <PackAssignDropdown
                          options={GENDER_OPTIONS}
                          value={selected.gender || "autre"}
                          onChange={changeGender}
                          disabled={saving}
                          fixedMenuWidth={150}
                          openBelow
                          ariaLabel="Genre du personnage"
                        />
                      </div>
                      <hr className="library-characters__rule" />
                      <h3 className="library-characters__subtitle">Suppression du personnage</h3>
                      <p className="library-characters__hint">
                        {`Pour supprimer le personnage et les passages auxquels il est lié (${
                          selected.passages?.length || 0
                        }), tapez le nom de ce personnage et cliquez sur le bouton supprimer.`}
                      </p>
                      <div className="library-characters__rename-row">
                        <p className="library-characters__label library-characters__label-spacer" aria-hidden="true">
                          Personnage
                        </p>
                        <input
                          className="library-characters__name"
                          value={deleteName}
                          maxLength={80}
                          aria-label="Confirmation du nom du personnage"
                          autoComplete="off"
                          onChange={(event) => setDeleteName(event.target.value)}
                        />
                      </div>
                      <div className="library-characters__editor-actions">
                        <button
                          type="button"
                          className="library-characters__button library-characters__button--ok"
                          disabled={saving || deleteName !== selected.name}
                          onClick={removeCharacter}
                        >
                          Supprimer
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="library-characters__empty">{listError || "Aucun personnage."}</p>
                  )}
              </div>
              ) : (
              <CharacterScrollPane
                className="library-characters__pane--passages"
                scrollerClassName="library-characters__passages"
                scrollRef={passagesScrollRef}
                restoreKey={bookId ? `passages:${bookId}:${selectedId}` : ""}
                restoreTop={readView(bookId).passagesScroll}
                contentKey={`${selectedId}:${visiblePassages.length}:${passageOrder ? 1 : 0}`}
                onScrollPosition={(scrollTop) =>
                  writeView(bookId, { passagesScroll: Math.max(0, Math.round(scrollTop)) })
                }
              >
                {actionError ? <p className="library-characters__error">{actionError}</p> : null}
                {visiblePassages.length ? (
                  visiblePassages.map((passage) => (
                    <div key={passage.highlightId} className="library-characters__passage">
                      <p className="library-characters__passage-text">{passage.quote}</p>
                      <div className="library-characters__passage-actions">
                        <MenuIconTooltip
                          tooltip={MENU_LIBRARY_TOOLTIPS.openPassage}
                          placement="above"
                          portal
                          zIndex={20}
                        >
                          <button
                            type="button"
                            className="library-characters__icon-btn"
                            aria-label={MENU_LIBRARY_TOOLTIPS.openPassage}
                            onClick={() => {
                              const highlight = highlightsRef.current.find(
                                (item) => item.id === passage.highlightId
                              ) || {
                                id: passage.highlightId,
                                quote: passage.quote,
                                kind: passage.kind,
                              };
                              if (!pickBook) {
                                onShowPassage(highlight);
                                return;
                              }
                              const place = readLibraryPlace(bookId);
                              writeLibraryPlace(bookId, {
                                anchor: highlightAnchor(highlight),
                                fontSize: place?.fontSize,
                                percent: place?.percent,
                              });
                              onOpenBook(bookId);
                            }}
                          >
                            <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                          </button>
                        </MenuIconTooltip>
                        <span className="library-characters__passage-tail">
                        <MenuIconTooltip
                          tooltip={MENU_LIBRARY_TOOLTIPS.reassignPassage}
                          placement="above"
                          portal
                          zIndex={20}
                        >
                          <button
                            type="button"
                            className="library-characters__icon-btn"
                            aria-label={MENU_LIBRARY_TOOLTIPS.reassignPassage}
                            onClick={() => openReassign(passage, selected)}
                          >
                            <RotateCcw size={16} strokeWidth={1.75} aria-hidden="true" />
                          </button>
                        </MenuIconTooltip>
                        <MenuIconTooltip
                          tooltip={MENU_LIBRARY_TOOLTIPS.unassignPassage}
                          placement="above"
                          portal
                          zIndex={20}
                        >
                          <button
                            type="button"
                            className="library-characters__icon-btn"
                            aria-label={MENU_LIBRARY_TOOLTIPS.unassignPassage}
                            onClick={() => unassign(passage.highlightId)}
                          >
                            <X size={16} strokeWidth={1.75} aria-hidden="true" />
                          </button>
                        </MenuIconTooltip>
                        </span>
                      </div>
                    </div>
                  ))
                ) : selected ? (
                  <p className="library-characters__empty">
                    {normalizedPassageQuery
                      ? "Aucun passage ne contient ce terme."
                      : "Aucun passage."}
                  </p>
                ) : null}
              </CharacterScrollPane>
              )}
            </div>
          ) : (
            <form className="library-characters__form" onSubmit={submitForm}>
              {mode === "reassign" ? (
                <>
                  <p className="library-characters__label">
                    Passage anciennement attribué au personnage :
                  </p>
                  <p className="library-characters__former">{previousName}</p>
                  <p className="library-characters__label">et maintenant attribué au personnage</p>
                  <div className="library-characters__picker">
                    <PackAssignDropdown
                      options={reassignOptions}
                      value={characterId}
                      placeholder={
                        reassignOptions.length ? "choisir un personnage" : "aucun autre personnage"
                      }
                      onChange={setCharacterId}
                      disabled={!reassignOptions.length}
                      fixedMenuWidth={260}
                      openBelow
                      menuClassName="library-characters__dropdown-menu"
                      ariaLabel="Choisir un personnage"
                    />
                  </div>
                </>
              ) : (
                <>
                  {mode === "create" ? (
                    <div className="library-characters__identity">
                      <div className="library-characters__field library-characters__field--name">
                        <p className="library-characters__label">Nom du personnage</p>
                        <input
                          className="library-characters__name"
                          value={name}
                          maxLength={80}
                          autoFocus
                          aria-label="Nom du personnage"
                          onChange={(event) => setName(event.target.value)}
                        />
                      </div>
                      <div className="library-characters__field library-characters__field--genre">
                        <p className="library-characters__label">Genre</p>
                        <div className="library-characters__genre">
                          <PackAssignDropdown
                            options={GENDER_OPTIONS}
                            value={gender}
                            placeholder="choisir"
                            onChange={setGender}
                            fixedMenuWidth={150}
                            openBelow
                            ariaLabel="Genre du personnage"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="library-characters__label">Personnage</p>
                  )}
                  {mode === "create" ? null : (
                    <div className="library-characters__picker">
                      <PackAssignDropdown
                        options={options}
                        value={characterId}
                        placeholder="choisir un personnage"
                        onChange={setCharacterId}
                        fixedMenuWidth={260}
                        openBelow
                        menuClassName="library-characters__dropdown-menu"
                        ariaLabel="Choisir un personnage"
                      />
                    </div>
                  )}
                  <p className="library-characters__label">Passage</p>
                </>
              )}
              <div className="library-characters__quote">{assignHighlight?.quote || ""}</div>
              {formError ? <p className="library-characters__error">{formError}</p> : null}
              <div className="library-characters__actions">
                <button
                  type="button"
                  className="library-characters__button library-characters__button--cancel"
                  onClick={cancelForm}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="library-characters__button library-characters__button--ok"
                  disabled={!canValidate || saving}
                >
                  Valider
                </button>
              </div>
            </form>
          )}
        </section>
      ) : null}
    </div>,
    portalRoot
  );
}
